import styled, { css } from 'styled-components'

export interface NumericInputStyledProps {
  $hasPrefix?: boolean
  $hasSuffix?: boolean
  $disabled?: boolean
}

export const Container = styled.div.withConfig({
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
`

export interface InputWrapperStyledProps {
  $disabled?: boolean
}

export const InputWrapper = styled.div.withConfig({
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})<InputWrapperStyledProps>`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  transition: ${({ theme }) => theme.transitions.default};

  &:focus-within {
    border-color: ${({ theme }) => theme.colors.border.focus};
    box-shadow: ${({ theme }) => theme.shadows.focus};
  }

  ${({ $disabled, theme }) =>
    $disabled &&
    css`
      background: ${theme.colors.background.tertiary};
      border-color: ${theme.colors.border.light};
    `}
`

export const Prefix = styled.span.withConfig({
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})`
  display: flex;
  align-items: center;
  padding: 0 ${({ theme }) => theme.spacing.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  background: ${({ theme }) => theme.colors.background.secondary};
  border-right: 1px solid ${({ theme }) => theme.colors.border.light};
  user-select: none;
  flex-shrink: 0;
`

export const Suffix = styled.span.withConfig({
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})`
  display: flex;
  align-items: center;
  padding: 0 ${({ theme }) => theme.spacing.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  background: ${({ theme }) => theme.colors.background.secondary};
  border-left: 1px solid ${({ theme }) => theme.colors.border.light};
  user-select: none;
  flex-shrink: 0;
`

export const Input = styled.input<NumericInputStyledProps>`
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  padding: ${({ theme }) => theme.components.input.padding};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-family: ${({ theme }) => theme.typography.fontFamily.sans};
  color: ${({ theme }) => theme.colors.text.primary};
  transition: ${({ theme }) => theme.transitions.default};
  min-width: 0;

  ${({ $hasPrefix }) =>
    $hasPrefix &&
    css`
      padding-left: 0;
    `}

  ${({ $hasSuffix }) =>
    $hasSuffix &&
    css`
      padding-right: 0;
    `}

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.tertiary};
  }

  &:disabled {
    color: ${({ theme }) => theme.colors.text.tertiary};
    cursor: not-allowed;
  }

  /* Remove default number input spinners */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type='number'] {
    -moz-appearance: textfield;
  }
`
